import { ColDef, type ICellRendererParams } from "ag-grid-community";
import { Stack, Typography } from "@mui/material";
import { NatGatewayIdCellRenderer } from "./NatGatewayIdCellRenderer";
import { jiraIssuesColumnDef } from "../../common/jiraIssuesColumnDef";
import { tagsColumnDef } from "../../common/tagsColumnDef";
import { smartTagsColumnDef } from "../../common/smartTagsColumnDef";
import { regionColumnDef } from "../../common/regionColumnDef";
import { forecastedEOMColumnDef } from "../../common/forecastedEOMColumnDef";
import { resourceCreatedByColumnDef } from "../../common/resourceCreatedByColumnDef";
import { resourceTotalSpendColumnDef } from "../../common/resourceTotalSpendColumnDef";
import { getResourceCreationTimeColumnDef } from "../../common/getResourceCreationTimeColumnDef";
import { resourceAccountColumnDef } from "../../common/resourceAccountColumnDef";
import { convertByteToSize } from "@/components/utils/helpers/convertByteToSize";
import { FilteredNatGatewayResourceItem } from "@/services//cloudchipr.api";
import { getResourceIdentifierColumnDef } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/getResourceIdentifierColumnDef";
import { taskColumnDef } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/taskColumnDef";
import { billingCostColumnDef } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/billingCostColumnDef";
import { getListingPriceColumnDef } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/listingPriceColumnDef";
import { getResourceMetricsColumnDef } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/getResourceMetricsColumnDef";

export const natGatewayColumnDefs: ColDef[] = [
  getResourceIdentifierColumnDef(),

  {
    field: "resource.nat_gateway_id",
    headerName: "NAT Gateway ID",
  },

  forecastedEOMColumnDef,
  billingCostColumnDef,
  getListingPriceColumnDef("nat_gateway"),

  resourceAccountColumnDef,

  resourceTotalSpendColumnDef,

  {
    field: "resource.processed_data_value",
    headerName: "Processed Data",
    cellRenderer: ({
      data,
    }: ICellRendererParams<FilteredNatGatewayResourceItem>) => {
      const value = data?.resource?.processed_data_value ?? 0;
      const days = data?.resource?.processed_data_days;

      return (
        <Stack>
          <Typography variant="body2">{convertByteToSize(value)}</Typography>

          {days !== null && (
            <Typography variant="caption" color="text.secondary" fontSize={10}>
              {days === 0 ? "for today" : `for the last ${days} days`}
            </Typography>
          )}
        </Stack>
      );
    },
  },

  getResourceMetricsColumnDef(
    "Connections Sum",
    "total_connection_sum",
    "nat_gateway",
  ),

  getResourceCreationTimeColumnDef("Launch time"),

  resourceCreatedByColumnDef,
  {
    field: "resource.public_addresses",
    headerName: "Public IPv4 Address",
    cellRenderer: NatGatewayIdCellRenderer,
    valueGetter: ({ data }) =>
      data?.resource?.public_addresses?.join(", ") ?? "",
  },
  { field: "resource.vpc", headerName: "VPC" },
  {
    field: "resource.network_interface_ids",
    headerName: "Network Interface ID",
    cellRenderer: NatGatewayIdCellRenderer,
    valueGetter: ({ data }) =>
      data?.resource?.network_interface_ids?.join(", ") ?? "",
  },
  {
    field: "resource.private_addresses",
    headerName: "Private IPv4 Address",
    cellRenderer: NatGatewayIdCellRenderer,
    valueGetter: ({ data }) =>
      data?.resource?.private_addresses?.join(", ") ?? "",
  },
  { field: "resource.connectivity_type", headerName: "Connectivity Type" },

  regionColumnDef,
  tagsColumnDef,
  smartTagsColumnDef,
  taskColumnDef,
  jiraIssuesColumnDef,
];
